import { createContext, useState, useEffect, useCallback, useRef } from "react";
import apiClient from "../api/client";
import { getMe, logout as apiLogout } from "../api/auth";
import {
  AUTH_TAB_SYNC_EVENTS,
  broadcastAuthTabSync,
  subscribeAuthTabSync,
} from "../utils/authTabSync";

export const AuthContext = createContext(null);

export default function AuthProvider({ children }) {
  const [player, setPlayer] = useState(null);
  const [loading, setLoading] = useState(true);
  const playerRef = useRef(player);
  const hydratedTokenRef = useRef(null);
  const restoreGenerationRef = useRef(0);
  playerRef.current = player;

  const clearTokensIfStillCurrent = useCallback((failedAccessToken) => {
    apiClient.syncTokensFromStorage();
    const currentToken = apiClient.accessToken;
    if (!currentToken || currentToken === failedAccessToken) {
      apiClient.clearTokens();
      setPlayer(null);
      hydratedTokenRef.current = null;
    }
  }, []);

  const hydratePlayerFromSession = useCallback(async () => {
    const data = await getMe();
    hydratedTokenRef.current = apiClient.accessToken;
    setPlayer(data.player);
    return data.player;
  }, []);

  const restoreSessionIfNeeded = useCallback(() => {
    apiClient.syncTokensFromStorage();
    const currentToken = apiClient.accessToken;

    if (!currentToken) {
      if (playerRef.current) {
        setPlayer(null);
        hydratedTokenRef.current = null;
      }
      return;
    }

    if (playerRef.current && hydratedTokenRef.current === currentToken) {
      return;
    }

    const generation = restoreGenerationRef.current;
    const tokenAtStart = currentToken;

    void hydratePlayerFromSession().catch(() => {
      if (generation !== restoreGenerationRef.current) {
        return;
      }
      clearTokensIfStillCurrent(tokenAtStart);
    });
  }, [hydratePlayerFromSession, clearTokensIfStillCurrent]);

  // Wire up API client callbacks for cross-tab auth sync and forced logout
  useEffect(() => {
    apiClient.onAuthChange = (p) => {
      setPlayer(p);
      if (!p) {
        hydratedTokenRef.current = null;
      }
    };
    apiClient.onAuthRestore = restoreSessionIfNeeded;
    return () => {
      apiClient.onAuthChange = null;
      apiClient.onAuthRestore = null;
    };
  }, [restoreSessionIfNeeded]);

  // BroadcastChannel fallback alongside storage events
  useEffect(() => {
    return subscribeAuthTabSync((type) => {
      if (type === AUTH_TAB_SYNC_EVENTS.LOGOUT) {
        apiClient.syncTokensFromStorage();
        if (!apiClient.accessToken) {
          setPlayer(null);
          hydratedTokenRef.current = null;
        }
        return;
      }

      if (type === AUTH_TAB_SYNC_EVENTS.LOGIN) {
        apiClient.syncTokensFromStorage();
        restoreSessionIfNeeded();
      }
    });
  }, [restoreSessionIfNeeded]);

  // On mount, try to restore session from stored token
  useEffect(() => {
    const generation = ++restoreGenerationRef.current;

    async function restore() {
      apiClient.syncTokensFromStorage();
      if (!apiClient.accessToken) {
        setLoading(false);
        return;
      }

      const tokenAtStart = apiClient.accessToken;
      try {
        await hydratePlayerFromSession();
      } catch {
        if (generation !== restoreGenerationRef.current) {
          return;
        }
        clearTokensIfStillCurrent(tokenAtStart);
      } finally {
        if (generation === restoreGenerationRef.current) {
          setLoading(false);
        }
      }
    }

    restore();
  }, [hydratePlayerFromSession, clearTokensIfStillCurrent]);

  // Re-hydrate when returning to a tab that missed the storage event
  useEffect(() => {
    const handleVisibilityOrFocus = () => {
      if (document.visibilityState !== "visible") {
        return;
      }
      apiClient.syncTokensFromStorage();
      restoreSessionIfNeeded();
    };

    document.addEventListener("visibilitychange", handleVisibilityOrFocus);
    window.addEventListener("focus", handleVisibilityOrFocus);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityOrFocus);
      window.removeEventListener("focus", handleVisibilityOrFocus);
    };
  }, [restoreSessionIfNeeded]);

  const loginWithTokens = useCallback((accessToken, refreshToken, playerData) => {
    restoreGenerationRef.current += 1;
    apiClient.setTokens(accessToken, refreshToken);
    hydratedTokenRef.current = accessToken;
    setPlayer(playerData);
    broadcastAuthTabSync(AUTH_TAB_SYNC_EVENTS.LOGIN);

    // Auth responses return a minimal player object. Hydrate full capabilities
    // immediately so permission-gated UI (e.g. Admin Panel) updates right away.
    void hydratePlayerFromSession().catch(() => {
      // Keep the optimistic player fallback if hydration fails transiently.
    });
  }, [hydratePlayerFromSession]);

  const logout = useCallback(async () => {
    restoreGenerationRef.current += 1;
    await apiLogout();
    hydratedTokenRef.current = null;
    setPlayer(null);
    broadcastAuthTabSync(AUTH_TAB_SYNC_EVENTS.LOGOUT);
  }, []);

  const value = {
    player,
    loading,
    isAuthenticated: !!player,
    loginWithTokens,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
